import React, { useState, useEffect, useRef } from "react";

// components
import { Button } from "./Button";
import { ButtonIcon } from "./ButtonIcon";
import { Modal } from "./Modal";

// third party
import { HorizonButton, SideNav, HorizonText, TextVariant } from "@urbanpiper-engineering/horizon";
import ProgressBar from "@urbanpiper-engineering/horizon/dist/atoms/progressbar";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import history from "../../history";
import { Transition, config, animated } from "react-spring/renderprops";

// store
import { store } from "../../store/configureStore";

// utils
import { openFreshchatWidget, isChristmasSeason, isInternalUser, lS, trackEvent } from "../../atlas-utils";

// actions
import { ActionTypes } from "../../actions/_types";
import { fetchBizPlatforms } from "../../actions/actions";

// helpers
import getSideNavItems from "../../helpers/sideNav";

// config
import { ONBOARDING_FLOWS, ATLAS_ONBOARDING_STEPS, periscopePopUpVariants } from "../../client-config";
import { CoachMark } from "react-coach-mark";
import posthog from "posthog-js";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";

const SideNavContainer = ({
	classes = "",
	collapsed = false,
	toggleCollapsed,
	biz = {},
	access = {},
	billingRecoveryState,
	merakiWebSubscription,
	periscopeEarlyAccessState = {},
	atlasOnboardingState = {},
	sidebarState,
	freshChatWidgetState,
	isNavigationV2 = false,
	bizPlatforms,
	routerProps
}) => {
	const [isModalOpen, setModalOpen] = useState(false);
	const [openItems, setOpenItems] = useState(null);
	const isOnboardingFlowEnabled = atlasOnboardingState?.flowData?.name === ONBOARDING_FLOWS.ATLAS_ONBOARDING_FLOW;
	const { pathname } = useLocation();

	const handleOpenItems = () => {
		const items = rearrangedItems();
		let newOpenItems = [];

		items.some((item) => {
			const itemRegex = new RegExp(`^${item?.path?.split("?")?.[0] ?? ""}`, "g");
			if (item?.render && item?.path && pathname.match(itemRegex) !== null) {
				newOpenItems = [item.key];
				return true;
			}

			if (item?.render && item?.childrenProps?.length) {
				item.childrenProps.some((child) => {
					const childRegex = new RegExp(`^${child?.path?.split("?")?.[0] ?? ""}`, "g");

					if (child?.render && child?.path && pathname.match(childRegex) !== null) {
						newOpenItems = [item.key, child.key];
						return true;
					}
				});
			}
		});

		store.dispatch({
			type: "UPDATE_OPEN_ITEMS",
			payload: newOpenItems
		});
	};

	useEffect(() => {
		handleOpenItems();
	}, [pathname, merakiWebSubscription]);

	useEffect(() => {
		setOpenItems(sidebarState?.openItems);
	}, [sidebarState?.openItems]);

	useEffect(() => {
		fetchBizPlatforms();
	}, []);

	const rearrangedItems = () => {
		const items = getSideNavItems({
			biz,
			access,
			merakiWebSubscription,
			periscopeEarlyAccessState,
			atlasOnboardingState,
			billingRecoveryState,
			bizPlatforms,
			handleModalOpen,
			handleBillingAlertModal
		});

		const filteredItems = items
			.map((item) => {
				if (item?.childrenProps?.length) {
					item.childrenProps = item.childrenProps.filter((child) => !!child?.render);
				}
				return item;
			})
			.filter((item) => !!item?.render);

		return filteredItems;
	};

	const handleNavItemClick = (openNavItems) => {
		const items = rearrangedItems();
		const parentNav = items?.find((item) => item?.key === openNavItems?.[0]);
		const getNavWithPath = parentNav?.childrenProps?.length
			? parentNav?.childrenProps?.find((nav) => nav.key === openNavItems?.[openNavItems?.length - 1])
			: parentNav;

		if (getNavWithPath?.onClick) {
			getNavWithPath.onClick();
		}
	};

	const handleBillingAlertModal = () => {
		// show billing alert modal if any module is locked and
		// restricted due to pending or overdue invoices
		store.dispatch({
			type: ActionTypes.UPDATE_BILLING_RECOVERY_MODAL_STATE,
			payload: {
				isOpen: true
			}
		});
	};

	const handleModalOpen = () => {
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
	};

	const handleSetupMeraki = () => {
		window.open(
			"https://www.urbanpiper.com/meraki?utm_source=Atlas&utm_medium=referral&utm_campaign=Atlas-in-app&utm_id=In-app"
		);
	};

	const renderSideNavHeader = () => {
		return <LogoBlock collapsed={collapsed} redirectTo={isOnboardingFlowEnabled ? "/onboarding" : "/"} />;
	};

	const renderSideNavFooter = () => {
		return <FreshchatWidgetBlock collapsed={collapsed} freshChatWidgetState={freshChatWidgetState} />;
	};

	const renderSideNavCustomBody = () => {
		if (isOnboardingFlowEnabled) {
			return (
				<OnboardingProgress
					collapsed={collapsed}
					atlasOnboardingState={atlasOnboardingState}
					isMultibrandEnabled={biz?.isMultibrandEnabled}
				/>
			);
		}
		return null;
	};

	return (
		<div className={"side-nav-container " + classes}>
			<SideNavCoachMark bizId={biz?.id} />

			{openItems && (
				<SideNav
					className={
						!collapsed
							? "side-nav-comp" + (isNavigationV2 ? " v2" : "")
							: "side-nav-comp side-nav-comp--collapsed" + (isNavigationV2 ? " v2" : "")
					}
					activeKey={openItems}
					collapsed={collapsed}
					sideNavItems={rearrangedItems()}
					onSideNavItemClick={handleNavItemClick}
					customBody={renderSideNavCustomBody()}
					v2={isNavigationV2}
					toggleCollapsed={toggleCollapsed}
					routerProps={routerProps}
				/>
			)}
			<SetupMeraki
				isModalOpen={isModalOpen}
				handleModalClose={handleModalClose}
				handleSubmit={handleSetupMeraki}
			/>
		</div>
	);
};
const mapStateToProps = (store) => ({
	biz: store.login.loggedInbizDetail,
	access: store.login.loginDetail.access,
	billingRecoveryState: store.billingRecoveryState,
	merakiWebSubscription: store.merakiWebSubscription.data,
	periscopeEarlyAccessState: store.periscopeEarlyAccessState,
	atlasOnboardingState: store.atlasOnboardingState,
	freshChatWidgetState: store.freshChatWidgetState,
	sidebarState: store.sidebarState,
	bizPlatforms: store.configItems.bizPlatforms
});
export default connect(mapStateToProps)(SideNavContainer);

export const LogoBlock = ({ collapsed = false, redirectTo }) => {
	const handleSnowfall = (active = false) => {
		// handle snowfall animation during christmas season
		if (isChristmasSeason()) {
			store.dispatch({
				type: ActionTypes.UPDATE_SNOWFALL_STATE,
				payload: {
					active
				}
			});
		}
	};
	return (
		<div
			className={"atlas-logo-container" + (collapsed ? " collapsed" : "")}
			onMouseEnter={() => handleSnowfall(true)}
			onMouseLeave={() => handleSnowfall(false)}
		>
			<Link to={redirectTo}>
				<img className="atlas-logo-img img-first" src="/assets/icons/atlas-logo.svg" />
				{!collapsed && (
					<img className="atlas-logo-img" src="/assets/header-icons/atlas-text-logo.svg" alt="atlas-logo" />
				)}
				{isChristmasSeason() && (
					<img
						className={collapsed ? "santa-hat-collapsed" : "santa-hat"}
						src="/assets/icons/icon-santa-hat.svg"
					/>
				)}
			</Link>
		</div>
	);
};

const FreshchatWidgetBlock = ({ collapsed, freshChatWidgetState }) => {
	return (
		<div
			className={
				collapsed
					? "freshchat-support-btn-container freshchat-left-nav-closed-get-help-btn"
					: "freshchat-support-btn-container"
			}
		>
			<Transition
				native
				from={{ transform: "scale(0)", opacity: 0 }}
				enter={{ transform: "scale(1)", opacity: 1 }}
				leave={{ transform: "scale(0)", opacity: 0 }}
				items={!freshChatWidgetState.busy}
				config={{ ...config.stiff, ...{ duration: 500 } }}
			>
				{(isOpen) =>
					isOpen &&
					((props) => (
						<animated.div style={{ ...props }}>
							{collapsed ? (
								<div onClick={openFreshchatWidget}>
									<img src="/assets/left-nav/icon-get-help.svg" />
								</div>
							) : (
								<Button
									clickHandler={openFreshchatWidget}
									classes="freshchat-help-button"
									loading={!freshChatWidgetState.loaded}
								>
									<ButtonIcon icon="freshchat" />
									<span>GET HELP</span>
								</Button>
							)}
						</animated.div>
					))
				}
			</Transition>
		</div>
	);
};

const SetupMeraki = ({ isModalOpen = false, handleSubmit, handleModalClose }) => {
	return (
		<Modal
			isOpen={isModalOpen}
			close={handleModalClose}
			title={"Trying to setup Meraki?"}
			classes="sidebar-modal"
			subTitle={"Enabling Meraki lets you generate customised website for your business"}
			showSubmitAction
			submitAction={handleSubmit}
			submitTitle="Learn more"
			submitButtonWidth="200"
			showCancelAction
			cancelAction={handleModalClose}
			cancelTitle="Cancel"
		>
			<div>
				<img src="/assets/icons/meraki-ad.png" className="meraki-ad-image" />
			</div>
			<div>
				Get your own <strong>branded website & app</strong> for your restaurant.
				<br /> Save on commissions and own your customer relationships.
			</div>
		</Modal>
	);
};

const OnboardingProgress = ({ collapsed = false, atlasOnboardingState, isMultibrandEnabled = false }) => {
	const getOnboardingProgressPercentage = () => {
		const completedSteps = atlasOnboardingState?.flowSteps
			?.filter((step) => step?.status === "complete")
			.map((step) => step?.step?.name);
		let percentage = 0;
		const taxFlow = atlasOnboardingState?.flowSteps?.find(
			(step) => step?.step?.name === ATLAS_ONBOARDING_STEPS.TAXES_CHARGES
		)
			? false
			: true;
		const weightageMap = !taxFlow
			? {
					[ATLAS_ONBOARDING_STEPS.COMPLETE_COMPANY_DETAILS]: isMultibrandEnabled ? 5 : 10,
					[ATLAS_ONBOARDING_STEPS.CREATE_BRANDS]: 5,
					[ATLAS_ONBOARDING_STEPS.CONFIGURE_LOCATIONS]: 15,
					[ATLAS_ONBOARDING_STEPS.CONFIGURE_MENUS]: 30,
					[ATLAS_ONBOARDING_STEPS.TAXES_CHARGES]: 10,
					[ATLAS_ONBOARDING_STEPS.GO_LIVE_ON_LOCATIONS]: 30,
					[ATLAS_ONBOARDING_STEPS.INVITE_USERS]: 5
				}
			: {
					[ATLAS_ONBOARDING_STEPS.COMPLETE_COMPANY_DETAILS]: isMultibrandEnabled ? 5 : 10,
					[ATLAS_ONBOARDING_STEPS.CREATE_BRANDS]: 5,
					[ATLAS_ONBOARDING_STEPS.CONFIGURE_LOCATIONS]: 30,
					[ATLAS_ONBOARDING_STEPS.CONFIGURE_MENUS]: 30,
					[ATLAS_ONBOARDING_STEPS.GO_LIVE_ON_LOCATIONS]: 25,
					[ATLAS_ONBOARDING_STEPS.INVITE_USERS]: 5
				};

		Object.keys(weightageMap).forEach((step) => {
			if (completedSteps.includes(step)) {
				percentage += weightageMap[step];
			}
		});

		return percentage;
	};

	return (
		<div className={"side-nav-onboarding-progress" + (collapsed ? " collapsed" : "")}>
			<div className="side-nav-onboarding-progress-row" onClick={() => history.push("/onboarding")}>
				<img className="side-nav-onboarding-user-icon" src="/assets/icons/user-blue.svg" />
				{!collapsed && (
					<React.Fragment>
						<HorizonText variant={TextVariant.BODY1} className="side-nav-onboarding-text">
							Setup Guide
						</HorizonText>
						<HorizonText variant={TextVariant.BODY1} className="side-nav-onboarding-percentage-text">
							{`${getOnboardingProgressPercentage()}%`}
						</HorizonText>
					</React.Fragment>
				)}
			</div>
			<ProgressBar
				className="side-nav-onboarding-progress-bar"
				progress={getOnboardingProgressPercentage()}
				width={100}
			/>
		</div>
	);
};

const SideNavCoachMark = ({ bizId }) => {
	const periscopeSideNavElement = document.querySelector('[data-state="periscope-early-access"]');
	const periscopePopupLs = lS.get("periscope")?.sideNavPopUp?.[bizId];

	let periscopePopUpRef = useRef(null);
	const coachRef = useRef(null);
	const periscope_intro_variant = posthog.getFeatureFlag("periscope_intro_popup_variant");
	const [activateNumber, setActivateNumber] = useState(-1);

	const popUpShown = (key, checked = false) => {
		if (key == "periscope") {
			let periscopeLocalStorage = lS.get("periscope") || {};

			const variables = {
				last: Date.now(),
				count: (periscopePopupLs?.count || 0) + 1,
				...(checked ? { value: true } : {})
			};
			periscopeLocalStorage = {
				...periscopeLocalStorage,
				sideNavPopUp: {
					...periscopeLocalStorage.sideNavPopUp,
					[bizId]: { ...periscopePopupLs, ...variables }
				}
			};

			lS.set("periscope", periscopeLocalStorage);
			setActivateNumber(-1);
		}
	};

	const trackTheEvent = (key, metadaData = {}) => {
		trackEvent(key, metadaData);
	};

	const periscopePopUpCondition =
		window.posthog?.isFeatureEnabled("enable-periscope") &&
		!window.posthog?.isFeatureEnabled("periscope-early-access") &&
		!lS?.get("periscope")?.data?.[bizId]?.bizSubscription &&
		!isInternalUser() &&
		!periscopePopupLs?.value &&
		(periscopePopupLs?.count || 0) <= 1 &&
		(periscopePopupLs?.last ? (Date.now() - periscopePopupLs.last) / (1000 * 3600 * 24) >= 7 : true);

	const removePeriscopeBgColor = () => {
		if (periscopeSideNavElement) {
			periscopeSideNavElement.style.removeProperty("background-color");
		}
	};
	const dismissBtnPeriscope = () => {
		trackTheEvent("periscope_popup_dismissed", { variant: periscope_intro_variant });
		popUpShown("periscope", false);
		removePeriscopeBgColor();
	};
	const KnowMoreBtnPeriscope = () => {
		trackTheEvent("periscope_popup_cta_clicked", { variant: periscope_intro_variant });
		popUpShown("periscope", true);
		history.push("/periscope/early-access");
		removePeriscopeBgColor();
		store.dispatch({
			type: ActionTypes.PERISCOPE_EARLY_ACCESS_STATE_CHANGE,
			payload: {
				sideNavPopClicked: true
			}
		});
	};

	const history = useHistory();
	const coachList = [
		{
			activate: periscopePopUpCondition,
			component: (
				<div className="periscope-awareness" ref={coachRef}>
					<img className="periscope-popup-awareness" src="/assets/periscope/periscope-popup-awareness.png" />
					<div className="text">
						<div className="head">{periscopePopUpVariants?.[periscope_intro_variant]?.heading}</div>
						<div className="desc">{periscopePopUpVariants?.[periscope_intro_variant]?.description}</div>
					</div>

					<div className="btns">
						<button className="dismiss" onClick={() => dismissBtnPeriscope()}>
							Dismiss
						</button>
						<HorizonButton type="primary" onClick={() => KnowMoreBtnPeriscope()}>
							Know more
						</HorizonButton>
					</div>
				</div>
			),
			reference: periscopePopUpRef,
			tooltip: { position: "right", margin: "" }
		}
	];
	useOnClickOutside(coachRef, (event) => handleClickOutside(event));
	const handleClickOutside = () => {
		popUpShown("periscope", false);
		removePeriscopeBgColor();
	};

	const periscopePopUp = () => {
		if (periscopeSideNavElement) {
			periscopePopUpRef.current = periscopeSideNavElement;
			trackTheEvent("periscope_popup_shown", { variant: periscope_intro_variant });
			setActivateNumber(0);
		}
	};

	useEffect(() => {
		if (periscopeSideNavElement && periscope_intro_variant && periscopePopUpCondition) {
			periscopePopUp();
			if (periscopeSideNavElement) {
				periscopeSideNavElement.style.backgroundColor = "#EAEAEA";
			}
		}
	}, [periscopeSideNavElement, periscope_intro_variant]);

	const clearHeight = useRef(null);
	const sideNav = document.querySelector(".side-nav-comp");

	const handleClick = () => {
		const pageContainer = document.querySelector(".page-container");
		if (pageContainer) {
			pageContainer.style.height = "130dvh";
		}
		const randomValue = Math.random() * 20 + 5;
		window.scroll(0, randomValue);

		if (clearHeight.current) {
			clearTimeout(clearHeight.current);
		}
		clearHeight.current = setTimeout(() => {
			if (pageContainer) {
				pageContainer.style.height = "";
			}
			clearHeight.current = null;
		}, 1000);
	};
	useEffect(() => {
		const coachMarkSelector = ".harsh-coach-mark";
		const tooltipSelector = ".hcm-tooltip-base";
		const tooltipRightSelector = ".hcm-tooltip-base-right";

		const addClasses = () => {
			const coachMark = document.querySelector(coachMarkSelector);
			const tooltip = document.querySelector(tooltipSelector);
			const tooltipRight = document.querySelector(tooltipRightSelector);

			if (coachMark) coachMark.classList.add("periscope-coachmark");
			if (tooltip) tooltip.classList.add("periscope-popup-content");
			if (tooltipRight) tooltipRight.classList.add("periscope-tooltip-edge");
		};

		const removeClasses = () => {
			const coachMark = document.querySelector(coachMarkSelector);
			const tooltip = document.querySelector(tooltipSelector);
			const tooltipRight = document.querySelector(tooltipRightSelector);

			if (coachMark) coachMark.classList.remove("periscope-coachmark");
			if (tooltip) tooltip.classList.remove("periscope-popup-content");
			if (tooltipRight) tooltipRight.classList.remove("periscope-tooltip-edge");
		};

		if (!periscope_intro_variant || !periscopePopUpCondition) {
			// Cleanup if conditions are false
			removeClasses();
			if (sideNav) {
				sideNav.removeEventListener("click", handleClick);
			}
			if (clearHeight.current) {
				clearTimeout(clearHeight.current);
			}
			return;
		}

		if (sideNav) {
			sideNav.addEventListener("click", handleClick);
		}

		const observer = new MutationObserver(() => {
			addClasses();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});

		// Initial check
		addClasses();

		return () => {
			observer.disconnect();
			removeClasses();
		};
	}, [periscope_intro_variant, periscopePopUpCondition, document.querySelector(".side-nav-comp")]);

	return (
		<>
			<div className="coach-mark-g">
				<CoachMark {...coachList[activateNumber]} />
			</div>
		</>
	);
};
